import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Lightbulb } from '../../../components/common/icons';
import { tips } from '../constants/tipDefinitions';
import { logger } from '../../../utils/logger';
import type { TipPosition, TipArrowAlign } from '../types';

interface TipTooltipProps {
  /** Tip ID（從 tipDefinitions 查找內容） */
  tipId: string;
  /** 關閉回調 */
  onClose: () => void;
  /** 目標元素 ref，Tooltip 會定位到此元素旁 */
  targetRef?: React.RefObject<HTMLElement>;
  /** 位置（可覆蓋定義） */
  position?: TipPosition;
  /** 箭頭對齊（可覆蓋定義） */
  arrowAlign?: TipArrowAlign;
  /** 自定義樣式 */
  className?: string;
}

// 箭頭位置：根據 position + arrowAlign 組合
function getArrowClasses(position: TipPosition, align: TipArrowAlign): string {
  const base = 'absolute w-2 h-2 bg-sky-900 rotate-45';

  if (position === 'top') {
    const horizontal = align === 'start' ? 'left-4' : align === 'end' ? 'right-4' : 'left-1/2 -translate-x-1/2';
    return `${base} -bottom-1 ${horizontal}`;
  }
  if (position === 'bottom') {
    const horizontal = align === 'start' ? 'left-4' : align === 'end' ? 'right-4' : 'left-1/2 -translate-x-1/2';
    return `${base} -top-1 ${horizontal}`;
  }
  if (position === 'left') {
    const vertical = align === 'start' ? 'top-3' : align === 'end' ? 'bottom-3' : 'top-1/2 -translate-y-1/2';
    return `${base} -right-1 ${vertical}`;
  }
  // right
  const vertical = align === 'start' ? 'top-3' : align === 'end' ? 'bottom-3' : 'top-1/2 -translate-y-1/2';
  return `${base} -left-1 ${vertical}`;
}

// 從元素往上遍歷 DOM，找到第一個有明確 z-index 的祖先，回傳該值
// 到 body 仍無結果時回傳 1（fallback）
function getEffectiveZIndex(el: HTMLElement): number {
  let current: HTMLElement | null = el;
  while (current && current !== document.body) {
    const z = getComputedStyle(current).zIndex;
    if (z !== 'auto') {
      const n = parseInt(z, 10);
      if (!isNaN(n)) return n;
    }
    current = current.parentElement;
  }
  return 1;
}

// 根據 anchor rect、position、arrowAlign 計算 fixed 座標
function calcFixedStyle(
  rect: DOMRect,
  position: TipPosition,
  align: TipArrowAlign
): React.CSSProperties {
  const GAP = 16; // tooltip 與目標的間距（px）

  let top: number;
  let left: number;

  if (position === 'top') {
    top = rect.top - GAP;
    // transform 會往上移，這裡給 anchor Y
  } else if (position === 'bottom') {
    top = rect.bottom + GAP;
  } else if (position === 'left') {
    top = rect.top + rect.height / 2;
  } else {
    // right
    top = rect.top + rect.height / 2;
  }

  if (position === 'top' || position === 'bottom') {
    if (align === 'start') left = rect.left;
    else if (align === 'end') left = rect.right;
    else left = rect.left + rect.width / 2;
  } else if (position === 'left') {
    left = rect.left - GAP;
  } else {
    left = rect.right + GAP;
  }

  // 用 translate 做最終對齊
  const translateMap: Record<TipPosition, Record<TipArrowAlign, string>> = {
    top: {
      start: 'translate(0%, -100%)',
      center: 'translate(-50%, -100%)',
      end: 'translate(-100%, -100%)',
    },
    bottom: {
      start: 'translate(0%, 0%)',
      center: 'translate(-50%, 0%)',
      end: 'translate(-100%, 0%)',
    },
    left: {
      start: 'translate(-100%, 0%)',
      center: 'translate(-100%, -50%)',
      end: 'translate(-100%, -100%)',
    },
    right: {
      start: 'translate(0%, 0%)',
      center: 'translate(0%, -50%)',
      end: 'translate(0%, -100%)',
    },
  };

  return {
    position: 'fixed',
    top,
    left,
    transform: translateMap[position][align],
  };
}

/**
 * TipTooltip 組件
 *
 * 現代風格的 Tips Tooltip UI
 * 使用 Portal + fixed 定位，完全脫離祖先 overflow 限制
 */
export const TipTooltip: React.FC<TipTooltipProps> = ({
  tipId,
  onClose,
  targetRef,
  position,
  arrowAlign,
  className = ''
}) => {
  const tipDef = tips[tipId];
  const anchorRef = useRef<HTMLDivElement>(null);
  const finalPosition = position || tipDef?.position || 'top';
  const finalAlign = arrowAlign || tipDef?.arrowAlign || 'center';
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties | null>(null);
  const [resolvedPosition, setResolvedPosition] = useState<TipPosition>(finalPosition);

  const updatePosition = useCallback(() => {
    // targetRef 優先；fallback 到零尺寸 anchor div（向後相容）
    const el = targetRef?.current ?? anchorRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    // 動態 z-index：從目標元素往上爬取第一個有明確 z-index 的祖先，取其值 +1
    // Tip 只需比它指示的元素高一層，不應無條件高於所有層級
    const effectiveZ = getEffectiveZIndex(targetRef?.current ?? anchorRef.current ?? el);

    // 有 targetRef 時，根據 anchor 在 viewport 的位置自動選擇展開方向：
    // 下半部 → 往上展開（遠離底部的 fixed 元素）；上半部 → 往下展開
    const autoPosition: TipPosition = targetRef?.current
      ? (rect.top + rect.height / 2 > window.innerHeight / 2 ? 'top' : 'bottom')
      : finalPosition;

    setResolvedPosition(autoPosition);
    setTooltipStyle({ ...calcFixedStyle(rect, autoPosition, finalAlign), zIndex: effectiveZ + 1 });
  }, [targetRef, finalPosition, finalAlign]);

  // 初始計算 + scroll/resize/layout 變化時重新計算
  useEffect(() => {
    if (!tipDef) return;
    if (!targetRef?.current && !anchorRef.current) return;

    // 首次計算：雙重 rAF 確保 React commit → browser paint 完成後再讀取座標
    // 解決 useEffect 可能在 paint 之前執行導致 anchor 位置偏移的問題
    // 後續的 layout 變化（如非同步資料載入改變頁面高度）由下方 ResizeObserver 處理
    // 注意：用兩個獨立變數追蹤，避免內層 rAF ID 覆寫外層導致 cleanup 失效
    let outerRafId: number;
    let innerRafId: number;
    outerRafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        updatePosition();
      });
    });

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // 監聽 anchor 周圍 layout 變化（例如頁面資料載入後高度改變）
    const observer = new ResizeObserver(updatePosition);
    observer.observe(document.body);

    // targetRef 有值時，監聽 document 上所有 transitionend 事件
    // 側邊欄收合是 CSS transition，transition 結束後重算按鈕位置
    // 用 document（而非 targetRef）因為 transition 發生在祖先元素（側邊欄容器）
    const handleTransitionEnd = () => updatePosition();
    if (targetRef?.current) {
      document.addEventListener('transitionend', handleTransitionEnd);
    }

    return () => {
      cancelAnimationFrame(outerRafId);
      cancelAnimationFrame(innerRafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('transitionend', handleTransitionEnd);
      observer.disconnect();
    };
  }, [tipDef, updatePosition]);

  // 找不到定義時記錄警告（放 useEffect 避免 StrictMode 下執行兩次）
  useEffect(() => {
    if (!tipDef) {
      logger.warn(`Tip definition not found: ${tipId}`);
    }
  }, [tipDef, tipId]);

  // 鍵盤操作
  useEffect(() => {
    if (!tipDef) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [tipDef, onClose]);

  if (!tipDef) {
    return null;
  }

  const tooltip = tooltipStyle ? (
    <div
      style={tooltipStyle}
      className={className || undefined}
      role="tooltip"
    >
      <div className="relative bg-sky-900 text-white text-sm pl-3 pr-2 py-2.5 rounded-lg shadow-lg min-w-[200px] max-w-xs w-max">
        <div className="flex items-start gap-2">
          <Lightbulb size={14} className="mt-0.5 shrink-0 text-amber-400" />
          <p className="flex-1 leading-snug">{tipDef.content}</p>
          <button
            onClick={onClose}
            className="shrink-0 p-0.5 rounded text-gray-400 hover:text-white transition-colors"
            aria-label="Close tip"
          >
            <X size={14} />
          </button>
        </div>

        {/* 箭頭 */}
        <div className={getArrowClasses(resolvedPosition, finalAlign)} />
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Anchor：零尺寸，僅用於計算位置 */}
      <div ref={anchorRef} aria-hidden="true" />
      {tooltip && ReactDOM.createPortal(tooltip, document.body)}
    </>
  );
};
